import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { Constants } from 'utilities/constants';

export const initialState = {
  selectedAuctionTxnOpps: [],
  selectedAuctionICBMStrategies: [],
  selectedAuctionTxnOppValuations: null,
  isCompleteAuctionModalOpen: false,
  auctionUWMapState: {
    zoom: 4,
    center: { lat: 30.2672, lng: -97.7431 },
    gestureHandling: 'auto', // Gesture Handling instead of scrollwheel for CTRL + Scroll [auto/cooperative]
    mapViewType: 'hybrid', // [hybrid/roadmap]
    autoNav: false,
    selectedMarkerLabel: '',
    isDrawing: false,
    polygon: null,
    initialized: false
  },
  auctionUWMarkerState: {
    selectedMarker: null,
    isInfoBoxOpen: false,
    isFetchingImages: false,
    selectedMarkerImages: [],
    uncapturedMarkers: []
  },
  auctionGridCurrentViewData: [], // Used to filter the map based on grid filters
  auctionGridState: null,
  viewValuationDialogIsOpen: false,
  viewValuationDialogProps: null,
  isFetchingAuctionUWData: false,
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'auctionUWSlice',
  initialState,
  reducers: {
    setSelectedAuctionTxnOpps: (state, action) => {
      state.selectedAuctionTxnOpps = action.payload;
    },
    setSelectedAuctionICBMStrategies: (state, action) => {
      state.selectedAuctionICBMStrategies = action.payload;
    },
    setSelectedAuctionTxnOppValuations: (state, action) => {
      state.selectedAuctionTxnOppValuations = action.payload;
    },
    setIsCompleteAuctionModalOpen: (state, action) => {
      state.isCompleteAuctionModalOpen = action.payload;
    },
    setAuctionUWMapState: (state, action) => {
      state.auctionUWMapState = { ...state.auctionUWMapState, ...action.payload };
    },
    setAuctionUWMarkerState: (state, action) => { // Triggers getImagesSaga
      state.auctionUWMarkerState = { ...state.auctionUWMarkerState, ...action.payload };
    },
    setAuctionGridCurrentViewData: (state, action) => { // These are used to filter the map when grid filter is applied
      state.auctionGridCurrentViewData = action.payload;
    },
    setAuctionGridState: (state, action) => { // These are used to maintain grid state when leaving underwrite tab and returning
      state.auctionGridState = action.payload;
    },
    toggleViewValuationDialogIsOpen: (state, action) => {
      state.viewValuationDialogIsOpen = !state.viewValuationDialogIsOpen;
    },
    setViewValuationDialogProps: (state, action) => {
      state.viewValuationDialogProps = action.payload;
    },
    setIsFetchingAuctionUWData: (state, action) => {
      state.isFetchingAuctionUWData = action.payload;
    },
    // Redux saga actions, no reducers
    runAutoCalc: () => { }, // Triggers calcProformaSaga
    completeAuction: () => { }, // Triggers completeAuctionSaga
    fetchTxnOppValuations: { // Triggers getTxnOppValuationsSaga
      reducer: () => { },
      prepare: (txnOppId, onSuccess) => ({ payload: { txnOppId, onSuccess } })
    }
  }
});

export default slice.reducer;

export const auctionUWActions = slice.actions;

// Memoized selectors
export const auctionUWSelectors = {
  viewValuationDialogIsOpenSelector: createSelector(
    state => state.auctionUWState.viewValuationDialogIsOpen,
    viewValuationDialogIsOpen => viewValuationDialogIsOpen
  ),
  selectedAuctionTxnOppsSelector: createSelector(
    state => state.auctionUWState.selectedAuctionTxnOpps,
    selectedAuctionTxnOpps => selectedAuctionTxnOpps
  ),
  uwTasksSelector: createSelector(
    selectedAuctionTxnOpps => selectedAuctionTxnOpps,
    selectedAuctionTxnOpps => {
      let totalUWTasks = selectedAuctionTxnOpps.filter(opp => opp.opportunityStatus !== Constants.OpportunityStatus.TERMINATED).length;
      let remainingUWTasks = 0;
      selectedAuctionTxnOpps.forEach(txnOpp => {
        if (txnOpp.opportunityStatus !== Constants.OpportunityStatus.TERMINATED && txnOpp.underwriteStatus == null) {
          remainingUWTasks++;
        }
      });
      let completedUWTasks = totalUWTasks - remainingUWTasks;
      return [totalUWTasks, remainingUWTasks, completedUWTasks];
    }
  ),
  driveByInspectionsSelector: createSelector(
    selectedAuctionTxnOpps => selectedAuctionTxnOpps,
    selectedAuctionTxnOpps => {
      let totalDriveByInspections = selectedAuctionTxnOpps.filter(opp => opp.opportunityStatus !== Constants.OpportunityStatus.TERMINATED).length;
      let remainingDriveByInspections = 0;
      selectedAuctionTxnOpps.forEach(txnOpp => {
        if (txnOpp.opportunityStatus !== Constants.OpportunityStatus.TERMINATED && !txnOpp.inspectionStatus !== 'Inspection Finalized') {
          remainingDriveByInspections++;
        }
      });
      let completedDriveByInspections = totalDriveByInspections - remainingDriveByInspections;
      return [totalDriveByInspections, remainingDriveByInspections, completedDriveByInspections];
    }
  ),
  auctionTitlesSelector: createSelector(
    selectedAuctionTxnOpps => selectedAuctionTxnOpps,
    selectedAuctionTxnOpps => {
      let totalTitles = selectedAuctionTxnOpps.filter(opp => opp.opportunityStatus !== Constants.OpportunityStatus.TERMINATED).length;
      let remainingTitles = 0;
      selectedAuctionTxnOpps.forEach(txnOpp => {
        if (txnOpp.opportunityStatus !== Constants.OpportunityStatus.TERMINATED && txnOpp.bid == null) {
          remainingTitles++;
        }
      });
      let importedTitles = totalTitles - remainingTitles;
      return [totalTitles, remainingTitles, importedTitles];
    }
  ),
  isFetchingAuctionUWDataSelector: createSelector(
    state => state.propertyUWState.isFetchingAuctionUWData,
    isFetchingAuctionUWData => isFetchingAuctionUWData
  ),
}
